<template>
  <div
    class="d-flex position-relative cursor-pointer"
    @click.exact="onCardClick()"
  >
    <!-- Embedded Layout (New Design) -->
    <v-container fluid v-if="player && embedded" class="px-1 py-2">
      <v-row>
        <v-col cols="12" class="d-flex align-center justify-space-between py-0">
          <div class="d-flex align-stretch flex-grow-1">
            <!-- Player Image -->
            <div class="d-flex rounded-lg align-end pt-1 px-1 pb-0 mr-3" :class="{'grey lighten-3': !$vuetify.theme.dark,'black lighten-3': $vuetify.theme.dark}">
              <img
                :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/' + player.playerId + '.png'"
                @error="onImageLoadFailure($event, 'player')"
                style="height: 100%; width: 40px; object-fit: cover;"
              />
            </div>
            <!-- Player Info -->
            <div class="d-flex flex-column flex-grow-1 justify-center">
              <div class="d-flex align-center mb-1">
                <router-link
                  :to="`/player/${player.playerId}`"
                  class="primary--text text-ellipsis mr-2 body-1 font-weight-bold"
                >
                  {{player.name ? player.name : player.playerName}}
                </router-link>
                <v-chip
                  class="monospace-font"
                  style="margin: 1px"
                  :color="getPositionChip(player.fieldPositionId ? player.fieldPositionId : player.positionId)"
                  text-color="white"
                  x-small
                >
                  {{ getPositionCode(player.fieldPositionId ? player.fieldPositionId : player.positionId) }}
                </v-chip>
              </div>
              <!-- Country Flag + Age -->
              <div class="d-flex align-center">
                <div class="d-flex rounded-lg align-center" style="padding: 2px 2px;" :class="{'grey lighten-3': !$vuetify.theme.dark,'black lighten-3': $vuetify.theme.dark}">
                  <DriblabCountryFlag
                    v-if="player.countryCode || player.cC"
                    :iso="player.countryCode || player.cC"
                    :scale="0.3"
                  />
                </div>
                <span class="body-2 primary--text ml-1" v-if="player.age">{{player.age}}yo</span>
              </div>
            </div>
          </div>
          <!-- Team Logo -->
          <div v-if="player.teamId && showTeam" class="ml-2">
            <v-tooltip bottom color="lightSecondary lighten-4" max-width="300" v-if="player.teamName">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="d-inline-block">
                  <router-link
                    :to="'/team/'+player.teamId"
                    class="nav-link d-inline-block"
                    @click.exact.stop="onTeamClick()"
                  >
                    <img
                      @error="onImageLoadFailure($event,'team')"
                      :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + player.teamId + '.png'"
                      style="height: 28px; width: 28px;"
                    />
                  </router-link>
                </div>
              </template>
              <span class="black--text body-1">{{player.teamName}}</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <!-- Original Layout (Non-Embedded) -->
    <v-container fluid v-if="player && !embedded" class="px-1 py-0">
      <v-row>
        <v-col cols="12" class="pl-2 pr-0 py-1 d-flex flex-column justify-center">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center justify-space-between">
              <router-link :to="`/player/${player.playerId}`"
              class="primary--text text-ellipsis mr-2 body-1 font-weight-bold"
              >{{player.name ? player.name : player.playerName}}</router-link>
              <v-chip class="monospace-font"
                style="margin: 1px"
                :color="getPositionChip(player.fieldPositionId ? player.fieldPositionId : player.positionId)"
                text-color="white"
                x-small

              >{{ getPositionCode(player.fieldPositionId) }}</v-chip>
              <v-chip 
                class="monospace-font"
                v-if="player.secondFieldPositionId"
                style="margin: 1px"
                :color="getPositionChip(player.secondFieldPositionId)"
                text-color="white"
                x-small
              >{{ getPositionCode(player.secondFieldPositionId) }}</v-chip>
            </div>
            <div class="d-flex" v-if="player.teamId && player.teamName && showTeam">
              <div class="my-1 z-3" @click.exact.stop="onTeamClick()">
                <router-link
                  :to="'/team/'+player.teamId"
                  class="nav-link d-flex align-center w-100"
                >
                  <img @error="onImageLoadFailure($event,'team')"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + player.teamId + '.png'"
                    style="height: 25px; width: 25px;"
                  />
                  <div
                    class="mx-2 body-2 text-ellipsis"
                  >{{player.teamName}}</div>
                </router-link>
              </div>
            </div>
          </div>
          <span class="body-2 primary--text subheader-font" v-if="showMarketValue">
            {{ $t('playerScout.value') }} ({{ currentUserCurrencySymbol }}): {{ player.marketValue * 1000 | formatCash(currentUserRate) }}
          </span>
          <span class="body-2 primary--text subheader-font" v-if="showInjury && isInjuried">
            {{ player.injuryReason }}
            <span class="error--text font-weight-medium" v-if="player.injuryDateTo">({{$t('general.until')}} <strong>{{player.injuryDateTo}}</strong>)</span>
            <span class="error--text font-weight-medium" v-if="player.injuryGamesLost && !player.injuryDateTo">(<strong>{{player.injuryGamesLost}}</strong> {{$t('general.gamesLost')}})</span>
          </span>
          <span class="body-2 primary--text subheader-font" v-if="showSuspension && isSuspended">
            {{ player.suspensionReason }}
            <span class="error--text font-weight-medium" v-if="player.suspensionDateTo">({{$t('general.until')}} <strong>{{player.suspensionDateTo}}</strong>)</span>
            <span class="error--text font-weight-medium" v-if="player.suspensionGamesLost && !player.suspensionDateTo">(<strong>{{player.suspensionGamesLost}}</strong> {{$t('general.gamesLost')}})</span>
          </span>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>

import PlayerService from "../../services/player/player.service";
import { getChipColorByPosition, getPositionCode, getCurrencies, formatCash } from "../../utils/functions";
import DriblabCountryFlag from "../base/DriblabCountryFlag";

export default {
  name: "PlayerDashboardItem",
  components: {
    DriblabCountryFlag
  },
  filters: {
  },
  props: {
    player: {
      type: Object,
      default: false
    },
    allowRouter: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    },
    showInjury:{
      type: Boolean,
      default: false
    },
    showSuspension:{
      type: Boolean,
      default: false
    },
    showTeam:{
      type: Boolean,
      default: true
    },
    showMarketValue:{
      type: Boolean,
      default: false
    }
  },
  filters: {
    formatCash(n,rate) {
      return formatCash(n * rate)
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    isInjuried() {
      return this.player.injuried;
    },
    isSuspended() {
      return this.player.suspended;
    },
    currentUserCurrency(){
      return this.$store.getters["auth/currency"];
    },
    currentUserRate(){
      let rate = this.currentUserCurrency === "EUR" ? 1 : this.$store.getters["exchange/rates"][this.$store.getters["auth/currency"]];
      return rate ? rate : 1;
    },
    currencies(){
      return getCurrencies();
    },
    currentUserCurrencySymbol(){
      let currentCurrencyObj = _.find(this.currencies,(c) => c.code === this.currentUserCurrency)
      return currentCurrencyObj.symbol;
    },
  },
  methods: {
    toOneDecimal: function(value) {
      return value && !isNaN(value) ? value.toFixed(1) : 0;
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    onImageLoadFailure(event, type) {
      if (type === "league" || type === "team") {
        event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
      }  else if (type === "player") {
        event.target.src =
          `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/0_${this.player && this.player.isMale === false ? 'f' : 'm'}.png`;
      };
    },
    onCardClick() {
    },
    onTeamClick() {
      this.$router.push({ name: "team", params: { id: this.player.teamId } });
    },
    postPlayerReport(playerId) {
      if(!this.currentUser.impostor){
        PlayerService.postPlayerReport(this.currentUser.id, playerId, "Search");
      }
    }
  }
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/views/player/PlayerDashboardItem.vue